import { IconPlus, IconTrash } from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BasicDatePicker,
  BasicTable,
  ScalrBreadcrumb,
  SingleBranchSelect,
} from '@/components/customComponents'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsList } from '@/components/ui/tabs'
import { CreateBranchGoalPlan } from '@/screens/targets/kpiGoalPlan/CreateBranchGoalPlan'
import { DateRange } from 'react-day-picker'

import type {
  ScalrTableColumn,
  ScalrTableData,
} from '@/components/customComponents'
import { createTab, TabConfig } from '@/components/TabSkeleton'
import { useKpiBranchGoalAssignments, useKpiUserGoalAssignments } from '@/hooks'
import { TranslationFunctionType } from '@/i18n'
import { CreateUserGoalPlan } from '@/screens/targets/kpiGoalPlan/CreateUserGoalPlan'
import { formatValueForKpi } from '@/utils'
import { DateType } from '@/utils/date'

const getKpiBranchGoalPlanColumns = (
  t: TranslationFunctionType
): ScalrTableColumn[] => [
  {
    accessorKey: 'branchName',
    header: t('targets.table.branch'),
    type: 'text',
  },
  {
    accessorKey: 'kpiGroup',
    header: t('targets.table.kpiGroup'),
    type: 'text',
  },
  {
    accessorKey: 'kpiNameWithUnit',
    header: t('targets.table.kpiName'),
    type: 'text',
  },
  {
    accessorKey: 'period',
    header: t('targets.table.period'),
    type: 'text',
  },
  {
    accessorKey: 'targetValue',
    header: t('targets.table.target'),
    type: 'text',
  },
  {
    accessorKey: 'createdAt',
    header: t('targets.table.createdAt'),
    type: 'text',
  },
]

const getKpiUserGoalPlanColumns = (
  t: TranslationFunctionType
): ScalrTableColumn[] => [
  {
    accessorKey: 'userFullName',
    header: t('targets.table.user'),
    type: 'text',
  },
  {
    accessorKey: 'branchName',
    header: t('targets.table.branch'),
    type: 'text',
  },
  {
    accessorKey: 'kpiGroup',
    header: t('targets.table.kpiGroup'),
    type: 'text',
  },
  {
    accessorKey: 'kpiNameWithUnit',
    header: t('targets.table.kpiName'),
    type: 'text',
  },
  {
    accessorKey: 'period',
    header: t('targets.table.period'),
    type: 'text',
  },
  {
    accessorKey: 'targetValue',
    header: t('targets.table.target'),
    type: 'text',
  },
  {
    accessorKey: 'bonusAmount',
    header: t('targets.table.bonus'),
    type: 'percentage',
  },
  {
    accessorKey: 'createdAt',
    header: t('targets.table.createdAt'),
    type: 'text',
  },
]

type ListSelections = {
  branch?: string // Branch name instead of Selectable
  dateRange?: DateRange
  dateFilterType?: DateType
}

const KPIGoalPlanPage = () => {
  const { t } = useTranslation()
  const [currentView, setCurrentView] = useState<
    'list' | 'addBranchKpiGoal' | 'addUserKpiGoal'
  >('list')
  const [activeTab, setActiveTab] = useState<'branch' | 'user'>('branch')
  const [listSelections, setListSelections] = useState<ListSelections>({
    branch: undefined,
    dateRange: undefined,
    dateFilterType: 'quarterly',
  })

  const {
    fetchBranchAssignments,
    branchAssignments,
    loading: branchLoading,
    deleteBranchAssignment,
  } = useKpiBranchGoalAssignments()

  const {
    fetchUserAssignments,
    userAssignments,
    loading: userLoading,
    deleteUserAssignment,
  } = useKpiUserGoalAssignments()

  // Fetch KPI assignments when branch selection or active tab changes
  useEffect(() => {
    if (activeTab === 'branch') {
      fetchBranchAssignments({ branchName: listSelections.branch })
    } else if (activeTab === 'user') {
      fetchUserAssignments({ branchName: listSelections.branch })
    }
  }, [
    listSelections.branch,
    activeTab,
    fetchBranchAssignments,
    fetchUserAssignments,
  ])

  // Handle date picker changes
  const handleDateChange = useCallback(
    (newDate: DateRange | undefined, newDateFilterType: DateType) => {
      setListSelections((prev) => ({
        ...prev,
        dateRange: newDate,
        dateFilterType: newDateFilterType,
      }))
    },
    []
  )

  const handleCancelCreate = useCallback(() => {
    setCurrentView('list')
  }, [])

  const handleSaveCreate = useCallback(() => {
    setCurrentView('list')
    if (activeTab === 'branch') {
      fetchBranchAssignments({ branchName: listSelections.branch })
    } else {
      fetchUserAssignments({ branchName: listSelections.branch })
    }
  }, [
    activeTab,
    fetchBranchAssignments,
    fetchUserAssignments,
    listSelections.branch,
  ])

  const handleDeleteBranchKPI = async (id: string | number) => {
    if (typeof id !== 'string') return
    const confirmDelete = window.confirm(
      t('targets.form.confirmDeleteKpiAssignment')
    )
    if (confirmDelete) {
      try {
        await deleteBranchAssignment(id)
      } catch (error) {
        console.error('Failed to delete KPI branch assignment:', error)
      }
    }
  }

  const handleDeleteUserKPI = async (id: string | number) => {
    if (typeof id !== 'string') return
    const confirmDelete = window.confirm(
      t('targets.form.confirmDeleteKpiAssignment')
    )
    if (confirmDelete) {
      try {
        await deleteUserAssignment(id)
      } catch (error) {
        console.error('Failed to delete KPI user assignment:', error)
      }
    }
  }

  const handleBranchSelect = useCallback((branchName: string) => {
    setListSelections((prev) => ({ ...prev, branch: branchName }))
  }, [])

  const branchAssignmentsData: ScalrTableData[] = useMemo(
    () =>
      branchAssignments && branchAssignments.length > 0
        ? branchAssignments.map((assignment) => ({
            id: assignment.id,
            kpiGroup: assignment.kpiGroup,
            kpiNameWithUnit: `${assignment.kpiName} (${assignment.kpiUnit})`,
            branchName: assignment.branchName,
            period: `Q${assignment.quarter} ${assignment.year}`,
            targetValue: formatValueForKpi(
              assignment.targetValue,
              assignment.kpiUnit
            ),
            bonusAmount: assignment.bonusAmount,
            createdAt: assignment.createdAt,
          }))
        : [],
    [branchAssignments]
  )

  const userAssignmentsData: ScalrTableData[] = useMemo(
    () =>
      userAssignments && userAssignments.length > 0
        ? userAssignments.map((assignment) => ({
            id: assignment.id,
            kpiGroup: assignment.kpiGroup,
            kpiNameWithUnit: `${assignment.kpiName} (${assignment.kpiUnit})`,
            branchName: assignment.branchName,
            userFullName: assignment.userFullName,
            period: `Q${assignment.quarter} ${assignment.year}`,
            targetValue: formatValueForKpi(
              assignment.targetValue,
              assignment.kpiUnit
            ),
            bonusAmount: assignment.bonusAmount,
            createdAt: assignment.createdAt,
          }))
        : [],
    [userAssignments]
  )

  // Filter assignments based on selected date range
  const filterAssignmentsByDate = useCallback(
    (assignments: ScalrTableData[]) => {
      return assignments.filter((assignment) => {
        // Filter by date range (year and quarter)
        if (listSelections.dateRange?.from) {
          // Extract year and quarter from period string "Q1 2024"
          const periodMatch = assignment.period
            ?.toString()
            .match(/Q(\d+) (\d+)/)
          if (!periodMatch) return true

          const assignmentQuarter = parseInt(periodMatch[1])
          const assignmentYear = parseInt(periodMatch[2])

          const fromYear = listSelections.dateRange.from.getFullYear()
          const fromQuarter =
            Math.floor(listSelections.dateRange.from.getMonth() / 3) + 1

          if (listSelections.dateFilterType === 'yearly') {
            if (assignmentYear !== fromYear) {
              return false
            }
          } else if (listSelections.dateFilterType === 'quarterly') {
            if (
              assignmentYear !== fromYear ||
              assignmentQuarter !== fromQuarter
            ) {
              return false
            }
          }
        }

        return true
      })
    },
    [listSelections]
  )

  const filteredBranchAssignmentsData = useMemo(
    () => filterAssignmentsByDate(branchAssignmentsData),
    [branchAssignmentsData, filterAssignmentsByDate]
  )

  const filteredUserAssignmentsData = useMemo(
    () => filterAssignmentsByDate(userAssignmentsData),
    [userAssignmentsData, filterAssignmentsByDate]
  )

  const tabConfigs: TabConfig[] = [
    {
      value: 'branch',
      title: t('targets.table.branch'),
      content: (
        <BasicTable
          data={filteredBranchAssignmentsData.map((item) => ({
            ...item,
            action: (
              <Button
                variant="ghost"
                onClick={() => handleDeleteBranchKPI(item.id as string)}
                size="sm"
                className="h-8 w-8 p-0 bg-white border-none"
              >
                <IconTrash className="text-red-500" />
              </Button>
            ) as any,
          }))}
          columns={[
            ...getKpiBranchGoalPlanColumns(t),
            {
              accessorKey: 'action',
              header: t('targets.table.action'),
              type: 'text',
            },
          ]}
          loading={branchLoading}
          enablePagination={true}
          itemsPerPage={6}
          showCard={true}
        />
      ),
    },
    {
      value: 'user',
      title: t('targets.table.user'),
      content: (
        <BasicTable
          data={filteredUserAssignmentsData.map((item) => ({
            ...item,
            action: (
              <Button
                variant="ghost"
                onClick={() => handleDeleteUserKPI(item.id as string)}
                size="sm"
                className="h-8 w-8 p-0 bg-white border-none"
              >
                <IconTrash className="text-red-500" />
              </Button>
            ) as any,
          }))}
          columns={[
            ...getKpiUserGoalPlanColumns(t),
            {
              accessorKey: 'action',
              header: t('targets.table.action'),
              type: 'text',
            },
          ]}
          loading={userLoading}
          enablePagination={true}
          itemsPerPage={6}
          showCard={true}
        />
      ),
    },
  ]

  const tabs = tabConfigs.map((config) => createTab(config))

  return (
    <Card className="w-full min-h-screen">
      <CardHeader>
        <CardTitle pageTitle>{t('targets.navigation.kpiGoalPlan')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ScalrBreadcrumb
          breadcrumbItems={[
            { label: t('targets.navigation.targets'), href: '/targets' },
          ]}
          title={t('targets.navigation.kpiGoalPlan')}
        />

        {currentView === 'list' ? (
          <>
            {/* List View */}
            <div className="my-3">
              <Card>
                <CardContent className="p-3">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                    {/* Filter Controls */}
                    <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
                      <SingleBranchSelect
                        selectedBranch={listSelections.branch}
                        onSelectBranch={handleBranchSelect}
                      />
                      <BasicDatePicker
                        value={listSelections.dateRange}
                        dateFilterType={listSelections.dateFilterType}
                        onChange={handleDateChange}
                        noMonths
                        yearsUnselectable
                      />
                    </div>

                    {/* Action Button */}
                    <div className="flex gap-4 justify-center md:justify-start items-center">
                      <Button
                        onClick={() => setCurrentView('addBranchKpiGoal')}
                        size="sm"
                      >
                        <IconPlus className="mr-2" />
                        {t('targets.form.addBranchKpiGoal')}
                      </Button>
                      <Button
                        onClick={() => setCurrentView('addUserKpiGoal')}
                        size="sm"
                      >
                        <IconPlus className="mr-2" />
                        {t('targets.form.addUserKpiGoal')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="mt-3">
              <Tabs
                value={activeTab}
                onValueChange={(value) =>
                  setActiveTab(value as 'branch' | 'user')
                }
              >
                <TabsList className="mb-4 w-full">
                  {tabs.map((tab) => tab.trigger)}
                </TabsList>

                {tabs.map((tab) => tab.content)}
              </Tabs>
            </div>
          </>
        ) : currentView === 'addBranchKpiGoal' ? (
          <CreateBranchGoalPlan
            onCancel={handleCancelCreate}
            onSave={handleSaveCreate}
          />
        ) : currentView === 'addUserKpiGoal' ? (
          <CreateUserGoalPlan
            onCancel={handleCancelCreate}
            onSave={handleSaveCreate}
          />
        ) : null}
      </CardContent>
    </Card>
  )
}

export default KPIGoalPlanPage
